import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import { Button } from "../components/ui/button";

import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

import CaseStudySlide from "../components/slides-ai-os/CaseStudySlide";
import ROIIntroSlide from "../components/slides-ai-os/ROIIntroSlide";
import RealROISlide from "../components/slides-ai-os/RealROISlide";

const ACCESS_KEY = "asha_case_studies_deck_access";

const caseStudies = [
  {
    title: "How Asha automated staff tasks for",
    titleHighlight: "Dental Art Care",
    imageType: "logo",
    image: "/assets/practicelogos/dentalartcare.png",
    stats: [
      { label: "Total ROI", value: "$25,569" },
      { label: "Production Increase", value: "$23,400" },
      { label: "Tasks Completed", value: "1,428" },
    ],
    description:
      "In just a few weeks, Asha's AI team booked 16 new patients and 36 appointments for existing patients — capturing opportunities that would have been missed after hours. Our insurance agent completed 506 verifications and handled 62 calls, saving the team 668 minutes on the phone. Meanwhile, AI Fax Manager automatically categorized 54 incoming faxes, freeing up staff to focus on patient care.",
    roiCards: [
      { label: "Total ROI", value: "$25,569", subtext: "production + labor savings" },
      { label: "New Patients Booked", value: "16", subtext: "captured automatically" },
      { label: "Staff Hours Saved", value: "11+", subtext: "from insurance calls alone" },
      { label: "Existing Appts Booked", value: "36", subtext: "without staff involvement" },
    ],
  },
  {
    title: "How Asha drove $65K in ROI for",
    titleHighlight: "Generational Dental Associates",
    imageType: "logo",
    image: "/assets/practiceimages/generational.png",
    stats: [
      { label: "Total ROI", value: "$65,040" },
      { label: "Production Increase", value: "$45,950" },
      { label: "Tasks Completed", value: "9,562" },
    ],
    description:
      "In a few months, Asha's AI Receptionist secured 23 new patients and booked 63 appointments for existing patients, including after-hours opportunities. The AI Insurance Verification agent ran 5,665 verifications and made 713 calls, saving the team 6,870 minutes on insurance calls. The AI Hygiene Recall agent booked 38 hygiene appointments and completed 865 outreach communications — all without staff intervention.",
    roiCards: [
      { label: "Total ROI", value: "$65,040", subtext: "comprehensive impact" },
      { label: "Verifications Run", value: "5,665", subtext: "fully automated" },
      { label: "Staff Minutes Saved", value: "6,870", subtext: "from insurance calls" },
      { label: "New Patients Booked", value: "23", subtext: "captured automatically" },
    ],
  },
  {
    title: "How Asha powered patient growth for",
    titleHighlight: "Marquette Dentistry",
    imageType: "logo",
    image: "/assets/practiceimages/marquette.png",
    stats: [
      { label: "Total ROI", value: "$72,798" },
      { label: "Production Increase", value: "$66,350" },
      { label: "Tasks Completed", value: "5,085" },
    ],
    description:
      "In a few months, Asha's AI Receptionist booked 49 new patients and handled 386 calls — including capturing after-hours opportunities that would have otherwise been missed. The AI Hygiene Recall agent secured 28 hygiene appointments and completed 2,634 communications, driving ongoing patient engagement without requiring any staff phone calls. The AI Insurance Verification agent completed 391 verifications, saving the team 236 minutes on insurance calls.",
    roiCards: [
      { label: "Total ROI", value: "$72,798", subtext: "comprehensive impact" },
      { label: "New Patients Booked", value: "49", subtext: "captured automatically" },
      { label: "Hygiene Comms", value: "2,634", subtext: "automated outreach" },
      { label: "Calls Handled", value: "386", subtext: "by AI receptionist" },
    ],
  },
  {
    title: "How Asha fueled growth for",
    titleHighlight: "Clovis Family Dentistry",
    imageType: "logo",
    image: "/assets/practicelogos/clovis.png",
    stats: [
      { label: "Total ROI", value: "$75,307" },
      { label: "Production Increase", value: "$71,250" },
      { label: "Tasks Completed", value: "2,642" },
    ],
    description:
      "In a few months, Asha's AI Receptionist captured 45 new patients and booked 107 appointments for existing patients — including many after-hours calls that would have gone unanswered. The AI also managed 910 patient calls, while the Hygiene Recall agent secured 16 appointments and completed 746 patient communications, keeping Clovis productive around the clock.",
    roiCards: [
      { label: "Total ROI", value: "$75,307", subtext: "comprehensive impact" },
      { label: "Production Increase", value: "$71,250", subtext: "from new & existing appts" },
      { label: "New Patients", value: "45", subtext: "automatically booked" },
      { label: "Calls Handled", value: "910", subtext: "by AI receptionist" },
    ],
  },
  {
    title: "How Asha streamlined operations for",
    titleHighlight: "Dentures & Dentistry Spokane",
    imageType: "logo",
    image: "/assets/practicelogos/ddsspokane.png",
    stats: [
      { label: "Total ROI", value: "$14,766" },
      { label: "Production Increase", value: "$13,250" },
      { label: "Tasks Completed", value: "1,035" },
    ],
    description:
      "In a few months, Asha's AI Receptionist booked 10 new patients and 17 appointments for existing patients, expanding access to care. The Insurance Verification agent processed 494 verifications and uploaded 240 benefits forms automatically — streamlining administrative workflows and enabling staff to focus on patient care.",
    roiCards: [
      { label: "Total ROI", value: "$14,766", subtext: "production + labor savings" },
      { label: "Benefits Forms Uploaded", value: "240", subtext: "fully automated" },
      { label: "Verifications Processed", value: "494", subtext: "without staff effort" },
      { label: "Additional Services Booked", value: "27", subtext: "new + existing patients" },
    ],
  },
  {
    title: "How Asha powered massive growth for",
    titleHighlight: "St. Charles Family Dentistry",
    imageType: "logo",
    image: "/assets/practicelogos/stcharles.png",
    stats: [
      { label: "Total ROI", value: "$188,663" },
      { label: "Production Increase", value: "$183,850" },
      { label: "Tasks Completed", value: "2,668" },
    ],
    description:
      "In a few months, Asha's AI Receptionist captured 204 new patients, ensuring every opportunity — including after-hours calls — was secured. The Insurance Verification agent processed 1,529 verifications, freeing up valuable staff time for patient care.",
    roiCards: [
      { label: "Total ROI", value: "$188,663", subtext: "comprehensive impact" },
      { label: "Production Increase", value: "$183,850", subtext: "from booked appointments" },
      { label: "New Patients Captured", value: "204", subtext: "including after-hours" },
      { label: "Verifications Completed", value: "1,529", subtext: "zero staff involvement" },
    ],
  },
  {
    title: "How Asha's AI team delivered results for",
    titleHighlight: "Premier Dentistry",
    imageType: "logo",
    image: "/assets/practicelogos/premier.png",
    stats: [
      { label: "Total ROI", value: "$50,650" },
      { label: "Production Increase", value: "$44,000" },
      { label: "Tasks Completed", value: "3,686" },
    ],
    description:
      "In a few months, Asha's AI Receptionist captured 30 new patients and booked 68 appointments for existing patients, handling 154 inbound calls — many answered outside regular office hours.",
    roiCards: [
      { label: "Total ROI", value: "$50,650", subtext: "production + labor savings" },
      { label: "Production Increase", value: "$44,000", subtext: "from booked appointments" },
      { label: "Staff Phone Time Saved", value: "795 min", subtext: "from automated insurance calls" },
      { label: "Patient Comms Automated", value: "742", subtext: "recalls & follow-ups" },
    ],
  },
  {
    title: "How Asha transformed",
    titleHighlight: "NTX Mental Health",
    image: "/assets/casestudyimage2.png",
    stats: [
      { label: "EHR", value: "PracticeQ" },
      { label: "Num Providers", value: "20+" },
      { label: "Specialties", value: "Psychiatry, Therapy, Mental Health" },
    ],
    description:
      "Asha developed a full team of AI agents hyper-specialized for NTX's practice: AI receptionist, AI patient intake agent, AI scribe, AI SMS agent, AI schedule optimizer, AI patient experience agent, AI insurance verification agent, and more.",
    roiCards: [
      { label: "Calls fully automated", value: "67%", subtext: "no human intervention" },
      { label: "Note-taking time eliminated", value: "82%", subtext: "with our AI scribe" },
    ],
  },
];

const slides = [
  { id: "roi-intro", title: "ROI & Impact", render: () => <ROIIntroSlide /> },
  ...caseStudies.map((cs, i) => ({
    id: `case-${i}`,
    title: cs.titleHighlight,
    render: () => <CaseStudySlide {...cs} />,
  })),
  { id: "roi-recap", title: "Real Practice Data", render: () => <RealROISlide /> },
];

const CaseStudiesDeckPage = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    if (hasStoredContentAccess(ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Deck access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    document.title = "Asha AI OS — Case Studies";
    const previousBg = document.body.style.background;
    document.body.style.background = "#0a0a0a";
    return () => {
      document.body.style.background = previousBg;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const parentWidth = containerRef.current.offsetWidth;
      const newScale = parentWidth / 1400;
      setScale(newScale);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const handler = (e) => {
      const tag = e.target && e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target && e.target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        api.scrollNext();
        e.preventDefault();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        api.scrollPrev();
        e.preventDefault();
      } else if (e.key === "Home") {
        api.scrollTo(0);
        e.preventDefault();
      } else if (e.key === "End") {
        api.scrollTo(slides.length - 1);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [api]);

  return (
    <div className="min-h-screen xl:h-screen w-screen bg-neutral-950 flex flex-col items-center p-3 md:p-6 overflow-x-hidden">
      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          sessionStorageKey={ACCESS_KEY}
        />
      )}

      {/* Top bar */}
      <div className="w-full max-w-[1800px] flex items-center justify-between gap-3 mb-4 z-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center p-1.5">
            <img
              src="/assets/alogo.png"
              alt="Asha"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-white tracking-tight">
              Asha Health
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/40 font-mono">
              AI OS · Case Studies
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-white/40">
          <span className="text-white/30">Confidential</span>
          <span className="text-white/20">·</span>
          <span className="text-white/30">Case studies</span>
        </div>
      </div>

      {/* Main slide container */}
      <div className="w-full max-w-[1800px] flex-1 flex items-center justify-center min-h-0">
        <div
          ref={containerRef}
          className="w-full max-w-[1400px] aspect-video relative"
        >
          <div className="w-full h-full overflow-hidden bg-transparent relative rounded-3xl shadow-2xl ring-1 ring-white/10">
            <div
              style={{
                width: "1400px",
                height: "787.5px",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <CarouselContent className="h-full ml-0">
                  {slides.map((s) => (
                    <CarouselItem key={s.id} className="h-full pl-0">
                      {s.render()}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="w-full max-w-[1400px] flex items-center justify-between gap-3 mt-4 z-50">
        <Button
          variant="outline"
          onClick={() => api?.scrollPrev()}
          disabled={current === 0}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Go to slide ${i + 1}: ${s.title}`}
                onClick={() => api?.scrollTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === current
                    ? "w-6 bg-blue-400"
                    : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/50 whitespace-nowrap">
            {String(current + 1).padStart(2, "0")} /{" "}
            {String(slides.length).padStart(2, "0")}{" "}
            <span className="text-white/30">·</span>{" "}
            <span className="text-white/80">{slides[current]?.title}</span>
          </div>
        </div>

        <Button
          onClick={() => api?.scrollNext()}
          disabled={current === slides.length - 1}
          className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-30 disabled:hover:bg-blue-600"
        >
          Next
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default CaseStudiesDeckPage;
